import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';
import { syncFormControlDisabledState } from 'common/utils/form-control.util';

export type InputType = 'number' | 'text' | 'date' | 'email' | 'tel' | 'password' | undefined;
type IconPosition = 'left' | 'right';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const noop: (...args: any[]) => any = () => null;


@Component({
    selector: 'utc-input',
    templateUrl: './input.component.html',
    styleUrls: ['./input.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class InputComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
    @Input() inputId: string;
    @Input() label: string;
    @Input() placeholder?: string;
    @Input() type?: InputType;
    @Input('value') _value?: string;
    @Input() min?: number;
    @Input() step?: number;
    @Input() hideLabel = false;
    @Input() disabled = false;
    @Input() ariaLabel?: string;
    @Input() required = false;
    // TODO: refactor forms
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    @Input() inputControl: any;
    @Input() labelIcon?: string;
    @Input() iconLabel?: string;
    @Input() iconPosition?: IconPosition;
    @Input() infoMessage: string;
    @Input() errorMessage: string | null;
    @Input() error: boolean;
    @Input() hasIconFunction: boolean;
    @Input() disableInputIcon?: boolean;
    @Input() disabledInput?: boolean;
    @Input() shadow: 'light' | 'default' | 'none' = 'default';
    @Input() size: 'default' | 'small' = 'default';
    @Input() mask?: boolean = false;
    @Input() numberMaxLength?: number;

    @Output() enterKeyFunction = new EventEmitter<string>();
    @Output() keystrokeFunction = new EventEmitter<Event>();
    @Output() blurFunction = new EventEmitter<string>();
    @Output() focusFunction = new EventEmitter<Event>();
    @Output() iconClickFunction = new EventEmitter<string>();

    @ViewChild('input') input: ElementRef;
    onChange = noop;
    onTouched = noop;
    isFocused = false;
    className: string;
    minimizeLabel: boolean;
    maxLength = 524288
    private maskListener: ((event: Event) => void) | null = null;

    constructor(
        private cdr: ChangeDetectorRef
    ) {
        this.type = 'text';
        this.inputId = '';
        this.label = '';
        this.placeholder = '';
        this._value = '';
        this.className = '';
    }

    get max(): number | undefined {
        return this.maxLength;
    }

    get value(): string | undefined {
        return this._value;
    }

    @Input()
    set max(val: number | undefined) {
        this.maxLength = val || 524288;
    }

    set value(val: string | undefined) {
        this._value = val;
        this.minimizeLabel = this._value !== '';
    }

    @HostListener('keydown', ['$event'])
    public keyListener(evt: KeyboardEvent) {
        const allowedKeys = ['ArrowLeft', 'ArrowRight', 'Delete', 'Backspace', 'Tab', 'Enter', 'Home', 'End'];
        const isPhoneField = this.inputId && (this.inputId.includes('office') || this.inputId.includes('phone') || this.inputId.includes('mobile') || this.inputId.includes('fax') || this.inputId.includes('toll'));
        const isDigit = (/^\d$/).test(evt.key);
        const isAllowedKey = allowedKeys.includes(evt.key) || evt.ctrlKey || evt.metaKey;

        if (!this.mask && isPhoneField) {
            if (!isDigit && !isAllowedKey) {
                evt.preventDefault();

                return;
            }
        }
        if (this.type === 'number' && this.numberMaxLength) {
            const inputValue = this.input.nativeElement.value;

            if (['e', 'E', '+', '-', '.'].includes(evt.key)) {
                evt.preventDefault();

                return;
            }

            if (!isDigit && !isAllowedKey) {
                evt.preventDefault();

                return;
            }

            if (isDigit && inputValue.length >= this.numberMaxLength) {
                evt.preventDefault();

                return;
            }
        }
        if (evt && evt.code && evt.code.toLowerCase() === 'enter') {
            this.enterKeyFunction.emit(this.input.nativeElement.value);
        }
        else {
            this.keystrokeFunction.emit(this.input.nativeElement.value);
        }
        this.minimizeLabel = this._value !== '';

        this.cdr.detectChanges();
    }

    getNativeInputType(type: InputType) {
        if (type === 'number' && this.numberMaxLength) {
            return 'text';
        }

        return type;
    }

    ngOnInit() {
        this.syncDisabledState();
        this.inputControl.registerOnDisabledChange((val: boolean) => {
            this.disabled = this.disabledInput || val;
        });

        this.className = `hvac-input hvac-input-${this.type}`;
        // eslint-disable-next-line no-self-assign
        this.placeholder = this.placeholder;
        this.writeValue(this.inputControl.value);
        this.inputControl.valueChanges.subscribe((value: string) => {
            let newValue = value;

            if (this.type === 'number' && this.numberMaxLength && newValue) {
                const digitsOnly = newValue.toString().replace(/\D/g, '');
                const truncatedValue = digitsOnly.substring(0, this.numberMaxLength);

                if (newValue !== truncatedValue) {
                    this.inputControl.setValue(truncatedValue, { emitEvent: false });
                    newValue = truncatedValue;
                }
            }

            if (this.mask && newValue) {
                const digits = newValue.replace(/\D/g, '');
                const x = digits.match(/(\d{0,3})(\d{0,3})(\d{0,4})/);

                const formatted = x
                    ? (x[2]
                        ? `(${x[1]}) ${x[2]}${x[3] ? ` ${x[3]}` : ''}`
                        : x[1])
                    : '';

                if (newValue !== formatted) {
                    this.inputControl.setValue(formatted, { emitEvent: false });
                    newValue = formatted;
                }
            }
            this.writeValue(newValue);
            this.minimizeLabel = this._value !== '';
        });
        if (this.mask && this.inputControl.value) {
            const maskedNumber = this.inputControl.value.toString().match(/(\d{3})(\d{3})(\d{4})/);
            (maskedNumber) ? this.inputControl.value = `(${maskedNumber[1]}) ${maskedNumber[2]} ${maskedNumber[3]}` : '';
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.disabledInput && this.inputControl) {
            this.syncDisabledState();
        }

        const phoneInput = this.input?.nativeElement;
        if (phoneInput) {
            if (this.maskListener) {
                phoneInput.removeEventListener('input', this.maskListener);
                this.maskListener = null;
            }
            if (this.mask) {
                this.maskListener = (elem) => {
                    const x = (elem.target as HTMLInputElement).value.replace(/\D/g, '').match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
                    (x) ? (elem.target as HTMLInputElement).value = x[2] ? `(${x[1]}) ${x[2]}${x[3] ? ` ${x[3]}` : ''}` : x[1] : '';
                };
                phoneInput.addEventListener('input', this.maskListener);
                const inputElement = phoneInput as HTMLInputElement;
                const digits = inputElement.value.replace(/\D/g, '');
                if (digits) {
                    const x = digits.match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
                    if (x) {
                        inputElement.value = x[2] ? `(${x[1]}) ${x[2]}${x[3] ? ` ${x[3]}` : ''}` : x[1];
                        this.inputControl?.setValue(inputElement.value, { emitEvent: false });
                    }
                }
            }
            else if (this.inputId && (this.inputId.includes('office') || this.inputId.includes('phone') || this.inputId.includes('mobile') || this.inputId.includes('fax') || this.inputId.includes('toll'))) {
                const inputElement = phoneInput as HTMLInputElement;
                const currentValue = inputElement.value;
                const hasPhoneFormatting = (/^\(\d{3}\)\s\d{3}[\s-]\d{4}$/).test(currentValue);
                if (hasPhoneFormatting) {
                    const unmaskedValue = currentValue.replace(/\D/g, '');
                    inputElement.value = unmaskedValue;
                    this.inputControl?.setValue(unmaskedValue, { emitEvent: false });
                    inputElement.dispatchEvent(new Event('input', { bubbles: true }));
                }
            }
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    registerOnChange(fn: any) {
        this.onChange = fn;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    registerOnTouched(fn: any) {
        this.onTouched = fn;
    }

    inputBlur() {
        this.isFocused = false;
        this.blurFunction.emit(this.input.nativeElement.value);
    }

    inputFocus() {
        this.isFocused = true;
        this.focusFunction.emit(this.input.nativeElement.value);
    }

    inputButtonClick() {
        this.iconClickFunction.emit(this.input.nativeElement.value);
    }

    writeValue(val: string) {
        this._value = val;
        this.minimizeLabel = this._value !== '';
    }

    ngOnDestroy() {
        if (this.maskListener) {
            const phoneInput = document.getElementById(`_${this.inputId}`);
            if (phoneInput) {
                phoneInput.removeEventListener('input', this.maskListener);
            }
            this.maskListener = null;
        }
    }

    private syncDisabledState(): void {
        syncFormControlDisabledState(this.inputControl, this.disabledInput);
        this.disabled = this.disabledInput || this.inputControl?.disabled;
    }
}
